import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { CopyIcon } from '@trello/nachos/icons/copy';
import { Textfield } from '@trello/nachos/textfield';
import { Tooltip } from '@trello/nachos/tooltip';

import styles from './TextFieldDescription.less';

export const TextFieldDescription: FunctionComponent<{
  label: string;
  value: string;
  description: ReactNode;
}> = ({ label, value, description }) => {
  const [isCopied, changeCopyState] = useState(false);

  const onClickCopyText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      changeCopyState(true);
      setTimeout(() => {
        changeCopyState(false);
      }, 1500);
    } catch (e) {
      // no-op
    }
  }, [value]);

  return (
    <div className={styles.formSection}>
      <Textfield
        className={styles.textfield}
        label={<label className={styles.label}>{label}</label>}
        value={value}
        readOnly={true}
        iconAfter={
          <Tooltip
            content={
              isCopied
                ? intl.formatMessage({
                    id: 'templates.app_management.copied-to-clipboard',
                    defaultMessage: 'Copied to clipboard',
                    description:
                      'Indicates successful copying of text to the clipboard.',
                  })
                : intl.formatMessage({
                    id: 'templates.app_management.copy-to-clipboard',
                    defaultMessage: 'Copy to clipboard',
                    description:
                      'Button text for copying text to clipboard in app management.',
                  })
            }
            position="top"
          >
            <Button
              className={styles.copyBtn}
              children={<CopyIcon size="small" />}
              onClick={onClickCopyText}
            />
          </Tooltip>
        }
      />
      <div className={styles.inputDescription}>{description}</div>
    </div>
  );
};
